import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface LoadingStateProps {
  variant: "table" | "card" | "list" | "table-compact" | "metrics" | "chart" | "form";
  count?: number;
  className?: string;
}

export function LoadingState( { variant, count, className }: LoadingStateProps ) {
  const defaultCount = variant === "card" ? 3 : 5;
  const itemCount = count ?? defaultCount;

  if ( variant === "table" ) {
    return (
      <div className={cn( "space-y-3", className )}>
        {Array.from( { length: itemCount } ).map( ( _, i ) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-12 w-12" />
            <Skeleton className="h-12 flex-1" />
            <Skeleton className="h-12 w-24" />
            <Skeleton className="h-12 w-32" />
            <Skeleton className="h-12 w-20" />
            <Skeleton className="h-12 w-16" />
          </div>
        ) )}
      </div>
    );
  }

  if ( variant === "card" ) {
    return (
      <div className={cn( "grid gap-6 md:grid-cols-2 lg:grid-cols-3", className )}>
        {Array.from( { length: itemCount } ).map( ( _, i ) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-2/3 mt-2" />
            </CardContent>
          </Card>
        ) )}
      </div>
    );
  }

  if ( variant === "table-compact" ) {
    return (
      <div className={cn( "space-y-2", className )}>
        {Array.from( { length: itemCount } ).map( ( _, i ) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-20" />
            <Skeleton className="h-10 w-24" />
          </div>
        ) )}
      </div>
    );
  }

  if ( variant === "metrics" ) {
    return (
      <div className={cn( "grid gap-6 md:grid-cols-2 lg:grid-cols-4", className )}>
        {Array.from( { length: itemCount } ).map( ( _, i ) => (
          <Card key={i}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-32 mb-2" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ) )}
      </div>
    );
  }

  if ( variant === "chart" ) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if ( variant === "form" ) {
    return (
      <div className={cn( "space-y-4", className )}>
        {Array.from( { length: itemCount } ).map( ( _, i ) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) )}
      </div>
    );
  }

  // variant === "list"
  return (
    <div className={cn( "space-y-4", className )}>
      {Array.from( { length: itemCount } ).map( ( _, i ) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      ) )}
    </div>
  );
}
